import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import {
  updateManyProductFilterOptions,
  updateProductFilterOption,
} from "@/lib/redux/productFilters/productFiltersSlice";
import {
  productFilterOptionSelectors,
  productFilterSelectors,
} from "@/lib/redux/store";
import { useCallback, useMemo } from "react";
import ClientCSSTransition from "@/lib/components/ClientCSSTransition";
import ClientTransitionGroup from "@/lib/components/ClientTransitionGroup";
import { Button, Container } from "./styles";
import { productFilterValueToLabel } from "@/lib/helpers";
import { useRouter } from "next/router";
import { useTranslation } from "@/lib/translations";

const transitionProps = {
  timeout: 200,
  classNames: "item",
  unmountOnExit: true,
  mountOnEnter: true,
};

const Facets: React.FC = () => {
  const { locale = "it" } = useRouter();

  const t = {
    removeAll: useTranslation("filters.removeAll", locale) || "",
  };
  const dispatch = useAppDispatch();

  const filters = useAppSelector((state) =>
    productFilterSelectors
      .selectAll(state)
      .filter((filter) => filter.id !== "catalog")
      .map((filter) => ({
        ...filter,
        options: productFilterOptionSelectors
          .selectAll(filter)
          .filter((option) => option.selected),
      }))
  );

  const selectedOptionsTotal = useMemo(() => {
    let total = 0;
    filters.forEach((filter) => {
      total += filter.options.length;
    });
    return total;
  }, [filters]);

  const unselectFilterOption = useCallback(
    (filterId: string, optionId: string) => {
      dispatch(
        updateProductFilterOption({
          filterId,
          optionId,
          changes: {
            selected: false,
          },
        })
      );
    },
    [dispatch]
  );

  const unselectAllFilterOptions = useCallback(() => {
    filters.forEach((filter) => {
      dispatch(
        updateManyProductFilterOptions({
          filterId: filter.id,
          updates: filter.options.map((option) => ({
            id: option.value,
            changes: {
              selected: false,
            },
          })),
        })
      );
    });
  }, [dispatch, filters]);

  return (
    <Container $filtersActive={selectedOptionsTotal > 0}>
      <ClientTransitionGroup component={null}>
        {filters.map((filter) => {
          return filter.options.map((option) => (
            <ClientCSSTransition
              key={`${filter.id}:${option.value}`}
              {...transitionProps}
            >
              <Button
                onClick={() => unselectFilterOption(filter.id, option.value)}
              >
                {productFilterValueToLabel(option.value)}
              </Button>
            </ClientCSSTransition>
          ));
        })}
        {selectedOptionsTotal > 0 && (
          <ClientCSSTransition key={`remove-all`} {...transitionProps}>
            <Button
              onClick={() => unselectAllFilterOptions()}
              colorScheme="light"
            >
              {t.removeAll}
            </Button>
          </ClientCSSTransition>
        )}
      </ClientTransitionGroup>
    </Container>
  );
};

export default Facets;
